import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Palmtree } from "lucide-react";
import type { TerracePreference } from "@/types/table";
import type { PlacementAvailability } from "@/lib/publicAvailability";
import { getPlacementHelpMessage, getPublicAvailabilityLabel } from "@/lib/publicAvailability";

interface TerracePreferenceSelectorProps {
  value: TerracePreference;
  onChange: (value: TerracePreference) => void;
  helpText?: string;
  compact?: boolean;
  hideNoPreference?: boolean;
  availability?: Partial<Record<TerracePreference, PlacementAvailability>>;
}

export function TerracePreferenceSelector({ value, onChange, helpText, compact = false, hideNoPreference = false, availability }: TerracePreferenceSelectorProps) {
  const indoorUnavailable = availability?.indoor && availability.indoor.status !== 'available';
  const terraceUnavailable = availability?.terrace && availability.terrace.status !== 'available';
  const selectedMessage = value === 'terrace'
    ? getPlacementHelpMessage('en terrasse', availability?.terrace)
    : value === 'indoor'
      ? getPlacementHelpMessage('en intérieur', availability?.indoor)
      : undefined;
  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <div className="flex items-center gap-2">
        <Palmtree className={compact ? "w-4 h-4 text-primary" : "w-5 h-5 text-primary"} />
        <Label className={compact ? "text-sm font-semibold" : "text-lg font-semibold"}>
          Préférence de placement
        </Label>
      </div>
      {helpText && (
        <p className={compact ? "text-xs text-muted-foreground" : "text-sm text-muted-foreground"}>
          {helpText}
        </p>
      )}
      <RadioGroup
        value={value}
        onValueChange={(v) => onChange(v as TerracePreference)}
        className="flex flex-wrap gap-2"
      >
        {!hideNoPreference && (
          <label
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
              value === 'no_preference'
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <RadioGroupItem value="no_preference" id="terrace-none" className="sr-only" />
            <span className={compact ? "text-xs" : "text-sm"}>Sans préférence</span>
          </label>
        )}
        <label
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${indoorUnavailable ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'} ${
            value === 'indoor'
              ? 'border-primary bg-primary/10'
              : 'border-border hover:border-primary/50'
          }`}
        >
          <RadioGroupItem value="indoor" id="terrace-indoor" className="sr-only" disabled={Boolean(indoorUnavailable)} />
          <span className={compact ? "text-xs" : "text-sm"}>En intérieur</span>
          {indoorUnavailable && <span className="text-xs text-muted-foreground">{getPublicAvailabilityLabel(availability?.indoor?.status ?? 'incompatible')}</span>}
        </label>
        <label
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${terraceUnavailable ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'} ${
            value === 'terrace'
              ? 'border-primary bg-primary/10'
              : 'border-border hover:border-primary/50'
          }`}
        >
          <RadioGroupItem value="terrace" id="terrace-yes" className="sr-only" disabled={Boolean(terraceUnavailable)} />
          <span className={compact ? "text-xs" : "text-sm"}>En terrasse</span>
          {terraceUnavailable && <span className="text-xs text-muted-foreground">{getPublicAvailabilityLabel(availability?.terrace?.status ?? 'incompatible')}</span>}
        </label>
      </RadioGroup>
      {selectedMessage && <p role="status" className="text-sm text-destructive">{selectedMessage}</p>}
    </div>
  );
}
